import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Button, Card, CardBody } from "reactstrap";
import { InputText } from "primereact/inputtext";
import { TreeSelect } from "primereact/treeselect";
import { InputTextarea } from "primereact/inputtextarea";
import { FileUpload } from "primereact/fileupload";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { triggerAlert } from "utils/CommonFunctions";
import { Dropdown } from "primereact/dropdown";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch } from "react-redux";
import { setCompanySidebarVisible } from 'store/actions';
import { Calendar } from "primereact/calendar";

import Swal from 'sweetalert2'

const CompanyEditForm = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('company_id');
  const toast = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();


  const [formData, setFormData] = useState({
    company_name: "",
    website: "",
    company_email: "",
    mobile_phone: "",
    founded_year: "",
    specialties: "",
    description: "",
    user_ids: "",
    is_private: false,
    street1: "",
    street2: "",
    postal_code: "",
    linkedin_url: "",
    facebook_url: "",
    twitter_url: "",
    x_url: "",
    created_by_name: "",
    created_at: "",
    updated_at: "",
    last_activity_type: "N/A",
    last_activity_date: "N/A",
    industry: null,
    company_size: null,
    category: null,
    group: null,
    city: null,
    state: null,
    country: null,
    label: null,
    address: "",
    notes: "",
    visible: false,
  });

  const [startyear, setStartyear] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [companySizes, setCompanySizes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [labels, setLabels] = useState([]); // Dynamic labels state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [initialAddress, setInitialAddress] = useState({}); // New state for initial address

  const fetchCompanyData = async () => {
    try {
      const response = await axios.get(`/api/v1/company/${id}/`);
      return response.data || {};
    } catch (error) {
      console.error("Error fetching company data:", error);
      throw error;
    }
  };

  const fetchIndustries = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-industry-type/`);
      const industries = response.data.results || response.data || [];
      return industries.map((item) => ({ name: item.industry_name || "", code: item.industry_id }));
    } catch (error) {
      console.error("Error fetching industries:", error);
      return [];
    }
  };

  const fetchCompanySizes = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-company-size/`);
      const sizes = response.data.results || response.data || [];
      return sizes.map((item) => ({ name: item.company_size || "", code: item.id }));
    } catch (error) {
      console.error("Error fetching company sizes:", error);
      return [];
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-categories/`);
      const categories = response.data.results || response.data || [];
      return categories.map((item) => ({
        key: item.category_id,
        label: item.name || item.description || "",
        data: item,
        children: item.subcategories?.map((sub) => ({
          key: sub.category_id,
          label: sub.name || sub.description || "",
          data: sub,
          children: sub.subcategories?.map((subSub) => ({
            key: subSub.category_id,
            label: subSub.name || subSub.description || "",
            data: subSub,
            children: subSub.subcategories || [],
          })) || [],
        })) || [],
      }));
    } catch (error) {
      console.error("Error fetching categories:", error);
      triggerAlert("error", "Error", "Failed to fetch categories");
      return [];
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-groups/`);
      const groups = response.data.results || response.data || [];
      return groups.map((item) => ({
        key: item.group_id,
        label: item.name || item.group_name || "",
        data: item,
        children: item.subgroups?.map((sub) => ({
          key: sub.group_id,
          label: sub.name || sub.group_name || "",
          data: sub,
          children: sub.subgroups?.map((subSub) => ({
            key: subSub.group_id,
            label: subSub.name || subSub.group_name || "",
            data: subSub,
            children: subSub.subgroups || [],
          })) || [],
        })) || [],
      }));
    } catch (error) {
      console.error("Error fetching groups:", error);
      triggerAlert("error", "Error", "Failed to fetch groups");
      return [];
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-countries/`);
      const countries = response.data.results || response.data || [];
      return countries.map((item) => ({ name: item.name || "", code: item.country_id }));
    } catch (error) {
      console.error("Error fetching countries:", error);
      return [];
    }
  };

  const fetchStates = async (countryId) => {
    try {
      if (!countryId) return [];
      const response = await axios.get(`/api/v1/get-all-states/?country_id=${countryId}`);
      const states = response.data.results || response.data || [];
      return states.map((item) => ({ name: item.name || "", code: item.state_id }));
    } catch (error) {
      console.error("Error fetching states:", error);
      return [];
    }
  };

  const fetchCities = async (stateId) => {
    try {
      const response = await axios.get(`/api/v1/get-all-cities/?state_id=${stateId}`);
      const cities = response.data.results || response.data || [];
      return cities.map((item) => ({ name: item.name || "", code: item.city_id }));
    } catch (error) {
      console.error("Error fetching cities:", error);
      return [];
    }
  };

  const fetchLabels = async () => {
    try {
      const response = await axios.get("/api/v1/get-all-address-label/");
      const labelsData = response.data.results || response.data || [];
      return labelsData.map((item) => ({
        name: item.address_label || item.description || "",
        code: item.id, // Use the 'id' field for the code
      }));
    } catch (error) {
      console.error("Error fetching labels:", error);
      triggerAlert("error", "Error", "Failed to fetch address labels");
      return [];
    }
  };

  const findNodeByKey = (nodes, key) => {
    for (const node of nodes) {
      if (node.key === key) return node;
      if (node.children) {
        const found = findNodeByKey(node.children, key);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [companyData, industriesData, companySizesData, categoriesData, groupsData, countriesData, labelsData] = await Promise.all([
          fetchCompanyData(),
          fetchIndustries(),
          fetchCompanySizes(),
          fetchCategories(),
          fetchGroups(),
          fetchCountries(),
          fetchLabels(), // Fetch labels from the API
        ]);

        setIndustries(industriesData);
        setCompanySizes(companySizesData);
        setCategories(categoriesData);
        setGroups(groupsData);
        setCountries(countriesData);
        setLabels(labelsData); // Set labels from API response

        let statesData = [];
        let citiesData = [];

        if (companyData.country) {
          statesData = await fetchStates(companyData.country);
          setStates(statesData);
          if (companyData.state) {
            citiesData = await fetchCities(companyData.state);
            setCities(citiesData);
          }
        }

        const selectedCategory = companyData.category ? findNodeByKey(categoriesData, companyData.category) : null;
        const selectedGroup = companyData.group ? findNodeByKey(groupsData, companyData.group) : null;

        const initialFormData = {
          company_name: companyData.company_name || "",
          website: companyData.website || "",
          company_email: companyData.company_email || "",
          mobile_phone: companyData.mobile_phone || "",
          founded_year: companyData.founded_year || "",
          specialties: companyData.specialties || "",
          description: companyData.description || "",
          user_ids: companyData.user_ids || "",
          is_private: companyData.private || false,
          street1: companyData.street_address || companyData.street1 || "",
          street2: companyData.street_address_line_2 || companyData.street2 || "",
          postal_code: companyData.pincode || companyData.postal_code || "",
          linkedin_url: companyData.linkedin_url || "",
          facebook_url: companyData.facebook_url || "",
          twitter_url: companyData.twitter_url || "",
          x_url: companyData.x_url || "",
          created_by_name: companyData.created_by_name || "",
          created_at: companyData.created_at ? moment(companyData.created_at).format("DD/MM/YYYY HH:mm") : " ",
          updated_at: companyData.updated_at ? moment(companyData.updated_at).format("DD/MM/YYYY HH:mm") : " ",
          last_activity_type: companyData.last_activity_type || " ",
          last_activity_date: companyData.last_activity_date ? moment(companyData.last_activity_date).format("DD/MM/YYYY HH:mm") : " ",
          industry: companyData.industry ? industriesData.find((item) => item.code === companyData.industry) || null : null,
          company_size: companyData.size ? companySizesData.find((item) => item.code === companyData.size) || null : null,
          category: selectedCategory,
          group: selectedGroup,
          city: companyData.city && citiesData.length ? citiesData.find((item) => item.code === companyData.city) || null : null,
          state: companyData.state && statesData.length ? statesData.find((item) => item.code === companyData.state) || null : null,
          country: companyData.country ? countriesData.find((item) => item.code === companyData.country) || null : null,
          label: companyData.label ? labelsData.find((label) => label.code === companyData.label) : null,
          notes: companyData.notes || "",
          address: [
            companyData.street_address || companyData.street1,
            companyData.street_address_line_2 || companyData.street2,
            companyData.city?.name || (citiesData.find((item) => item.code === companyData.city)?.name || ""),
            companyData.state?.name || (statesData.find((item) => item.code === companyData.state)?.name || ""),
            companyData.country?.name || (countriesData.find((item) => item.code === companyData.country)?.name || ""),
            companyData.pincode || companyData.postal_code,
            companyData.label ? labelsData.find((label) => label.code === companyData.label)?.name : "",
          ].filter(Boolean).join("\n"),
          visible: false,
        };

        setFormData(initialFormData);
        setInitialAddress({
          street1: initialFormData.street1,
          street2: initialFormData.street2,
          city: initialFormData.city,
          state: initialFormData.state,
          country: initialFormData.country,
          postal_code: initialFormData.postal_code,
          label: initialFormData.label,
          address: initialFormData.address,
        });

        if (companyData.founded_year) {
          setStartyear(new Date(parseInt(companyData.founded_year), 0, 1));
        }
      } catch (error) {
        console.error("Error in fetchInitialData:", error);
        setError("Failed to load data. Please try again.");
        triggerAlert("error", "Error", "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [id]);

  useEffect(() => {
    const updateStates = async () => {
      if (formData.country?.code) {
        const statesData = await fetchStates(formData.country.code);
        setStates(statesData);
        if (!statesData.some((state) => state.code === formData.state?.code)) {
          setFormData((prev) => ({ ...prev, state: null, city: null }));
          setCities([]);
        }
      } else {
        setStates([]);
        setCities([]);
        setFormData((prev) => ({ ...prev, state: null, city: null }));
      }
    };

    updateStates();
  }, [formData.country?.code]);

  useEffect(() => {
    const updateCities = async () => {
      if (formData.state?.code) {
        const citiesData = await fetchCities(formData.state.code);
        setCities(citiesData);
        if (citiesData.length > 0 && formData.city) {
          const defaultCity = citiesData.find((city) => city.code === formData.city?.code);
          if (defaultCity) {
            setFormData((prev) => ({ ...prev, city: defaultCity }));
          }
        }
      } else {
        setCities([]);
        setFormData((prev) => ({ ...prev, city: null }));
      }
    };

    updateCities();
  }, [formData.state?.code]);

  useEffect(() => {
    const updatedAddress = [
      formData.street1,
      formData.street2,
      formData.city?.name,
      formData.state?.name,
      formData.country?.name,
      formData.postal_code,
      formData.label?.name,
    ].filter(Boolean).join("\n");
    setFormData((prev) => ({ ...prev, address: updatedAddress }));
  }, [formData.street1, formData.street2, formData.city, formData.state, formData.country, formData.postal_code, formData.label]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePhoneNumber = (phoneNumber) => {
    const re = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/;
    return re.test(String(phoneNumber));
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = "Company name is required";
      valid = false;
    }

    if (formData.company_email && !validateEmail(formData.company_email)) {
      newErrors.company_email = "Invalid email format";
      valid = false;
    }

    if (formData.mobile_phone && !validatePhoneNumber(formData.mobile_phone)) {
      newErrors.mobile_phone = "Invalid phone number format";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to edit the content before saving?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
      backdrop: 'rgba(0,0,0,0.8)',
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: {
        popup: 'swal2-popup-custom'
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        popup.style.zIndex = '10000';
        const container = popup.closest('.swal2-container');
        if (container) {
          container.style.zIndex = '10000';
        }
      }
    });

    if (!result.isConfirmed) {
      return; // User cancelled, do nothing
    }

    try {
      const payload = {
        company_name: formData.company_name,
        website: formData.website,
        company_email: formData.company_email,
        mobile_phone: formData.mobile_phone,
        founded_year: formData.founded_year ? formData.founded_year : null,
        specialties: formData.specialties,
        industry: formData.industry?.code,
        industry_name: formData.industry?.name,
        size: formData.company_size?.code,
        description: formData.description,
        category: formData.category?.key,
        group: formData.group?.key,
        user_ids: formData.user_ids,
        private: formData.is_private,
        street_address: formData.street1,
        street_address_line_2: formData.street2,
        city: formData.city?.code,
        state: formData.state?.code,
        country: formData.country?.code,
        pincode: formData.postal_code,
        label: formData.label?.code, // Send the label's id
        linkedin_url: formData.linkedin_url,
        facebook_url: formData.facebook_url,
        twitter_url: formData.twitter_url,
        x_url: formData.x_url,
        notes: formData.notes,
      };
      await axios.put(`/api/v1/company/${id}/`, payload);
      Swal.fire({
        title: "Updated!",
        text: "Company has been updated successfully.",
        icon: "success"
      });
      dispatch(setCompanySidebarVisible(false));
      navigate("/companies-allactive");
    } catch (error) {
      console.error("Error updating company:", error);
      Swal.fire({
        title: "Error!",
        text: error.response?.data?.error || "Failed to update company",
        icon: "error"
      });
    }
  };

  const showCancel = () => {
    navigate("/companies-allactive");
    dispatch(setCompanySidebarVisible(false));  
  };

  const [uniqueUserId, setUniqueUserId] = useState("");
  useEffect(() => {
    const userInfo = JSON.parse(sessionStorage.getItem("atsuser-info"));
    if (userInfo && userInfo.user_details) {
      setUniqueUserId(userInfo.user_details.unique_user_id);
      setFormData((prev) => ({ ...prev, user_ids: userInfo.user_details.unique_user_id }));
    }
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <React.Fragment>
      <Toast ref={toast} />
      <div className="page-content create-ajob">
        <Container fluid>
          <div className="page-title-box">
            <Row className="align-items-center">
              <Col md={6}>
                <h1 className="page-title">Edit Company</h1>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item active">
                    Update company details to maintain accurate records and streamline business operations.
                  </li>
                </ol>
              </Col>
              <Col xl={6}>
                <div className="d-flex justify-content-end">
                  <button type="submit" className="btn btn-success me-2" onClick={handleSubmit}>
                    <i className="pi pi-save me-1"></i> Save
                  </button>
                  <Button
                    color="primary"
                    className="btn btn-primary waves-effect waves-light me-2 cancel-outlinebtn"
                    onClick={showCancel}
                  >
                    <i className="pi pi-times me-1"></i> Cancel
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <Row>
            <Col xl={6}>
              <Card className="bg-form">
                <CardBody>
                  <h4 className="card-title mb-2">Basic Information</h4>
                  <Row>
                    <Col xl={12}>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label htmlFor="company">Company <span className="text-danger">*</span></label>
                        </Col>
                        <Col xl={9}>
                          <InputText
                            id="company"
                            value={formData.company_name}
                            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                            className="w-full"
                          />
                          {errors.company_name && <small className="p-error">{errors.company_name}</small>}
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label htmlFor="website">Website</label>
                        </Col>
                        <Col xl={9}>
                          <InputText
                            id="website"
                            className="w-full"
                            value={formData.website}
                            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label htmlFor="email">Email</label>
                        </Col>
                        <Col xl={9}>
                          <InputText
                            id="email"
                            className="w-full"
                            value={formData.company_email}
                            onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                          />
                          {errors.company_email && <small className="p-error">{errors.company_email}</small>}
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label htmlFor="phno">Phone Number</label>
                        </Col>
                        <Col xl={9}>
                          <InputText
                            id="phno"
                            className="w-full"
                            maxLength={10}
                            value={formData.mobile_phone}
                            onChange={(e) => setFormData({ ...formData, mobile_phone: e.target.value })}
                          />
                          {errors.mobile_phone && <small className="p-error">{errors.mobile_phone}</small>}
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label htmlFor="EmpMang_YearDatePicker">Year Founded</label>
                        </Col>
                        <Col xl={9}>
                          <DatePicker
                            id="EmpMang_YearDatePicker"
                            className="form-select Mukta_Font SelectYearDatePicker"
                            selected={startyear}
                            onChange={(date) => {
                              setStartyear(date);
                              setFormData({ ...formData, founded_year: date ? moment(date).format("yyyy") : "" });
                            }}
                            showYearPicker
                            dateFormat="yyyy"
                            maxDate={new Date()}
                            placeholderText="Please select a Year"
                          />
                        </Col>
                      </Row>
                      <Row className="mt-2 mb-2 align-items-center">
                        <Col xl={3}>
                          <label htmlFor="specialties">Specialties</label>
                        </Col>
                        <Col xl={9}>
                          <InputText
                            id="specialties"
                            value={formData.specialties}
                            onChange={(e) => setFormData({ ...formData, specialties: e.target.value })}
                            className="w-full"
                          />
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col lg={3}>
                          <label htmlFor="category">Categories</label>
                        </Col>
                        <Col lg={9}>
                          <TreeSelect
                            id="category"
                            value={formData.category ? formData.category.key : null}
                            onChange={(e) => {
                              const selectedNode = findNodeByKey(categories, e.value);
                              setFormData({ ...formData, category: selectedNode });
                            }}
                            options={categories}
                            filter
                            className="w-full activejobdrop"
                            placeholder={categories.length ? "Select Category" : "No categories available"}
                          />
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col lg={3}>
                          <label htmlFor="group">Group</label>
                        </Col>
                        <Col lg={9}>
                          <TreeSelect
                            id="group"
                            value={formData.group ? formData.group.key : null}
                            onChange={(e) => {
                              const selectedNode = findNodeByKey(groups, e.value);
                              setFormData({ ...formData, group: selectedNode });
                            }}
                            options={groups}
                            filter
                            className="w-full activejobdrop"
                            placeholder={groups.length ? "Select Group" : "No groups available"}
                          />
                        </Col>
                      </Row>
                     
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            <Col xl={6}>
              <Card className="bg-form">
                <CardBody>
                  <h4 className="card-title mb-2">Company Details</h4>
                  <Row>
                    <Col xl={12}>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label htmlFor="industry">Industry</label>
                        </Col>
                        <Col xl={9}>
                          <Dropdown
                            id="industry"
                            value={formData.industry}
                            onChange={(e) => setFormData({ ...formData, industry: e.value })}
                            options={industries}
                            optionLabel="name"
                            filter
                            filterPlaceholder="Search Industry"
                            className="w-full border border-gray-400 rounded-md bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            placeholder={industries.length ? "Select Industry" : "No industries available"}
                            disabled={!industries.length}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label htmlFor="companySize">Company Size</label>
                        </Col>
                        <Col xl={9}>
                          <Dropdown
                            id="companySize"
                            value={formData.company_size}
                            onChange={(e) => setFormData({ ...formData, company_size: e.value })}
                            options={companySizes}
                            optionLabel="name"
                            filter
                            filterPlaceholder="Search Company Size"
                            className="w-full border border-gray-400 rounded-md bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            placeholder={companySizes.length ? "Select Company Size" : "No sizes available"}
                            disabled={!companySizes.length}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label htmlFor="overview">Overview</label>
                        </Col>
                        <Col xl={9}>
                          <InputTextarea
                            rows={4}
                            cols={30}
                            placeholder="Enter a description..."
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full"
                            style={{ resize: 'none' }}
                          />
                        </Col>
                      </Row>

                      <Row className="mb-2">
                        <Col lg={3}>
                          <label htmlFor="address">Address</label>
                        </Col>
                        <Col xl={9}>
                          <div className="p-field companie-add" style={{ position: "relative" }}>
                            <InputTextarea
                              id="address"
                              value={formData.address}
                              placeholder="Enter your address"
                              style={{ paddingRight: "2rem", resize: 'none' }}
                              rows={3}
                              cols={30}
                              className="w-full"
                              readOnly
                            />
                            <i
                              className="pi pi-pencil"
                              style={{
                                position: "absolute",
                                right: "20px",
                                top: "70%",
                                transform: "translateY(-50%)",
                                color: "#6c757d",
                                cursor: "pointer",
                              }}
                              onClick={() => setFormData((prev) => ({ ...prev, visible: true }))}
                            ></i>
                            <Dialog
                              header="Edit Address"
                              className="address-popup"
                              visible={formData.visible}
                              onHide={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  visible: false,
                                  street1: initialAddress.street1,
                                  street2: initialAddress.street2,
                                  city: initialAddress.city,
                                  state: initialAddress.state,
                                  country: initialAddress.country,
                                  postal_code: initialAddress.postal_code,
                                  label: initialAddress.label,
                                  address: initialAddress.address,
                                }));
                              }}
                              style={{ width: "30vw" }}
                              breakpoints={{ "960px": "75vw", "641px": "100vw" }}
                              footer={
                                <div>
                                  <Button
                                    label="OK"
                                    icon="pi pi-check"
                                    onClick={() => {
                                      setFormData((prev) => ({ ...prev, visible: false }));
                                      setInitialAddress({
                                        street1: formData.street1,
                                        street2: formData.street2,
                                        city: formData.city,
                                        state: formData.state,
                                        country: formData.country,
                                        postal_code: formData.postal_code,
                                        label: formData.label,
                                        address: formData.address,
                                      });
                                    }}
                                    className="p-button"
                                  > <i className="pi pi-check me-1"></i> Ok </Button>
                                </div>
                              }
                            >
                              <div className="sidebard-details">
                                <form>
                                  <Row className="mb-3">
                                    <Col lg={6} sm={12}>
                                      <div className="p-field">
                                        <label htmlFor="street1">Street 1</label>
                                        <InputText
                                          id="street1"
                                          value={formData.street1}
                                          onChange={(e) => setFormData((prev) => ({ ...prev, street1: e.target.value }))}
                                          placeholder="Enter Street 1"
                                          className="w-full activejobdrop"
                                        />
                                      </div>
                                    </Col>
                                    <Col lg={6}>
                                      <label htmlFor="street2">Street 2</label>
                                      <InputText
                                        id="street2"
                                        value={formData.street2}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, street2: e.target.value }))}
                                        placeholder="Enter Street 2 (optional)"
                                        className="w-full activejobdrop"
                                      />
                                    </Col>
                                  </Row>
                                  <Row className="mb-3">
                                    <Col lg={6}>
                                      <label htmlFor="country">Country</label>
                                      <Dropdown
                                        id="country"
                                        value={formData.country}
                                        onChange={async (e) => {
                                          setFormData((prev) => ({ ...prev, country: e.value, state: null, city: null }));
                                          const statesData = await fetchStates(e.value.code);
                                          setStates(statesData);
                                          setCities([]);
                                        }}
                                        options={countries}
                                        optionLabel="name"
                                        filter
                                        placeholder={countries.length ? "Select Country" : "No countries available"}
                                        className="w-full activejobdrop"
                                        disabled={!countries.length}
                                      />
                                    </Col>
                                    <Col lg={6}>
                                      <label htmlFor="state">State</label>
                                      <Dropdown
                                        id="state"
                                        value={formData.state}
                                        onChange={async (e) => {
                                          setFormData((prev) => ({ ...prev, state: e.value, city: null }));
                                          const citiesData = await fetchCities(e.value.code);
                                          setCities(citiesData);
                                        }}
                                        options={states}
                                        optionLabel="name"
                                        filter
                                        placeholder={states.length ? "Select State" : "No states available"}
                                        className="w-full activejobdrop"
                                        disabled={!states.length}
                                      />
                                    </Col>
                                  </Row>
                                  <Row className="mb-3">
                                    <Col lg={6}>
                                      <label htmlFor="city">City</label>
                                      <Dropdown
                                        id="city"
                                        value={formData.city}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, city: e.value }))}
                                        options={cities}
                                        optionLabel="name"
                                        filter
                                        placeholder={cities.length ? "Select City" : "No cities available"}
                                        className="w-full activejobdrop"
                                        disabled={!cities.length}
                                      />
                                    </Col>
                                    <Col lg={6}>
                                      <label htmlFor="postalCode">Postal Code</label>
                                      <InputText
                                        id="postalCode"
                                        value={formData.postal_code}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          if (/^\d{0,6}$/.test(value)) {
                                            setFormData((prev) => ({ ...prev, postal_code: value }));
                                          }
                                        }}
                                        placeholder="Enter Postal Code"
                                        className="w-full activejobpost"
                                        maxLength={6}
                                      />
                                    </Col>
                                  </Row>
                                  <Row className="mb-3">
                                    <Col lg={12}>
                                      <label htmlFor="label">Label</label>
                                      <Dropdown
                                        id="label"
                                        value={formData.label}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, label: e.value }))}
                                        options={labels}
                                        optionLabel="name"
                                        placeholder={labels.length ? "Select Label" : "No labels available"}
                                        className="w-full activejobdrop"
                                        disabled={!labels.length}
                                      />
                                    </Col>
                                  </Row>
                                </form>
                              </div>
                            </Dialog>
                          </div>
                        </Col>
                      </Row>

                       <Row className="mt-2 align-items-center mb-2">
                        <Col lg={6}>
                          <Row>
                            <Col xl={6}>
                              <label htmlFor="userId">User ID</label>
                            </Col>
                            <Col xl={6}>
                              <InputText
                                id="userId"
                                placeholder="Enter User ID"
                                value={uniqueUserId}
                                onChange={(e) => setUniqueUserId(e.target.value)}
                                className="w-full"
                                readOnly
                                disabled
                              />
                            </Col>
                          </Row>
                        </Col>
                        <Col lg={6}>
                          <Row>
                            <Col xl={12}>
                              <input
                                type="checkbox"
                                className="me-2"
                                checked={formData.is_private}
                                onChange={(e) => setFormData((prev) => ({ ...prev, is_private: e.target.checked }))}
                              />
                              <label htmlFor="isPrivate">Private</label>
                            </Col>
                          </Row>
                        </Col>
                      </Row>

                      {/* <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label htmlFor="notes">Notes</label>
                        </Col>
                        <Col xl={9}>
                          <InputTextarea
                            id="notes"
                            rows={4}
                            cols={30}
                            placeholder="Enter notes..."
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            className="w-full"
                            style={{ resize: 'none' }}
                          />
                        </Col>
                      </Row> */}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col xl={6}>
              <Card className="bg-form">
                <CardBody>
                  <h4 className="card-title mb-2">System Fields</h4>
                  <Row>
                    <Col xl={12}>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label className="block">Create Date</label>
                        </Col>
                        <Col xl={9}>
                          <Calendar
                            id="createDate"
                            value={formData.created_at ? moment(formData.created_at, "DD/MM/YYYY HH:mm").toDate() : null}
                            placeholder="N/A"
                            className="w-full"
                            disabled={true}
                            showIcon
                            icon="pi pi-calendar"
                            dateFormat="yy-mm-dd"
                          />
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label className="block">Edit Date</label>
                        </Col>
                        <Col xl={9}>
                          <Calendar
                            id="editDate"
                            value={formData.updated_at ? moment(formData.updated_at, "DD/MM/YYYY HH:mm").toDate() : null}
                            disabled={true}
                            className="w-full"
                            placeholder="N/A"
                            showIcon
                            icon="pi pi-calendar"
                            dateFormat="yy-mm-dd"
                          />
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label className="block">Created By</label>
                        </Col>
                        <Col xl={9}>
                          <InputText
                            id="createdBy"
                            className="w-full"
                            disabled={true}
                            value={formData.created_by_name}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label className="block">Last Activity Type</label>
                        </Col>
                        <Col xl={9}>
                          <InputText
                            value={formData.last_activity_type}
                            id="lastActivityType"
                            className="w-full"
                            placeholder="Type"
                            disabled={true}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label className="block">Last Activity Date</label>
                        </Col>
                        <Col xl={9}>
                          <Calendar
                            value={formData.last_activity_date ? moment(formData.last_activity_date, "DD/MM/YYYY HH:mm").toDate() : null}
                            id="lastActivityDate"
                            placeholder="N/A"
                            className="w-full"
                            disabled={true}
                            showIcon
                            icon="pi pi-calendar"
                            dateFormat="yy-mm-dd"
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            <Col xl={6}>
              <Card className="bg-form">
                <CardBody>
                  <h4 className="card-title">Social Links</h4>
                  <Row>
                    <Col xl={12}>
                      <Row className="align-items-center mt-2">
                        <Col xl={3}>
                          <label className="block">LinkedIn URL</label>
                        </Col>
                        <Col xl={9}>
                          <div style={{ position: "relative" }}>
                            <InputText
                              id="linkedIn"
                              className="w-full"
                              placeholder="https://www.linkedin.com"
                              value={formData.linkedin_url}
                              onChange={(e) => setFormData((prev) => ({ ...prev, linkedin_url: e.target.value }))}
                            />
                            <i
                              className="pi pi-linkedin"
                              style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            ></i>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label className="block">Facebook URL</label>
                        </Col>
                        <Col xl={9}>
                          <div style={{ position: "relative" }}>
                            <InputText
                              id="facebook"
                              className="w-full"
                              placeholder="https://www.facebook.com"
                              value={formData.facebook_url}
                              onChange={(e) => setFormData((prev) => ({ ...prev, facebook_url: e.target.value }))}
                            />
                            <i
                              className="pi pi-facebook"
                              style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            ></i>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label className="block">Twitter URL</label>
                        </Col>
                        <Col xl={9}>
                          <div style={{ position: "relative" }}>
                            <div>
                              <InputText
                                id="twitter"
                                className="w-full"
                                placeholder="https://www.twitter.com"
                                value={formData.twitter_url}
                                onChange={(e) => setFormData((prev) => ({ ...prev, twitter_url: e.target.value }))}
                              />
                              <i
                                className="pi pi-twitter"
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                }}
                              ></i>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mt-2 align-items-center">
                        <Col xl={3}>
                          <label className="block">X URL</label>
                        </Col>
                        <Col xl={9}>
                          <div style={{ position: "relative" }}>
                            <div>
                              <InputText
                                id="xUrl"
                                className="w-full"
                                placeholder="https://x.com"
                                value={formData.x_url}
                                onChange={(e) => setFormData((prev) => ({ ...prev, x_url: e.target.value }))}
                              />
                              <i
                                className="pi pi-at"
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                }}
                              ></i>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CompanyEditForm;